/**
 * An investor's externally held mutual funds, from their CAS statement.
 *
 * Ported from the user platform's `helpers/mfExternalCas.ts`, narrowed to what
 * an admin can legitimately do on someone else's behalf:
 *
 *   READ    mf_external/user/{uuid}/   whatever has already been imported
 *   IMPORT  mf_external/cas/parse/     a CAS PDF the admin has, by file or URL
 *   REQUEST mf_external/cas/generate/  ask CAMS to email the CAS to the investor
 *
 * DELIBERATELY NOT PORTED: the Gmail OAuth path (`mf_external/gmail/*`) and the
 * CDSL OTP path (`mf_external/cdsl/*`). Both authenticate as the INVESTOR — one
 * needs their Google account, the other an OTP sent to their mobile — so an
 * admin cannot complete either, and an OTP the investor reads out is exactly
 * the consent layer this panel is built without. They remain investor-only on
 * the user platform.
 */

import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "./api";

interface CasGain {
  absolute?: number | null;
  percentage?: number | null;
}

interface CasDematMutualFund {
  isin?: string | null;
  name?: string | null;
  units?: number | null;
  value?: number | null;
}

interface CasDematAccount {
  bo_id?: string | null;
  dp_name?: string | null;
  demat_type?: string | null;
  holdings?: {
    demat_mutual_funds?: CasDematMutualFund[] | null;
  } | null;
}

interface CasStatementScheme {
  isin?: string | null;
  name?: string | null;
  units?: number | null;
  value?: number | null;
  cost?: number | null;
  nav?: number | null;
  type?: string | null;
  gain?: CasGain | null;
}

interface CasStatementFolio {
  amc?: string | null;
  folio_number?: string | null;
  registrar?: string | null;
  schemes?: CasStatementScheme[] | null;
}

export interface MFExternalCasData {
  meta?: {
    cas_type?: string | null;
    generated_at?: string | null;
    statement_period?: {
      from?: string | null;
      to?: string | null;
    } | null;
  } | null;
  investor?: {
    name?: string | null;
  } | null;
  demat_accounts?: CasDematAccount[] | null;
  mutual_funds?: CasStatementFolio[] | null;
}

interface MFExternalCasStoredData {
  user_id?: string | null;
  cas_data?: MFExternalCasData | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface MFExternalCasResponse {
  success?: boolean;
  success1?: boolean;
  message?: string;
  user_id?: string;
  data?: MFExternalCasData | MFExternalCasStoredData | null;
}

interface CasApiEnvelope<T> {
  success?: boolean;
  message?: string;
  data?: T;
  error?: unknown;
  status_code?: number;
}

export interface MFExternalCasErrorInfo {
  message: string;
  requiresReconnect: boolean;
}

export interface CasExternalFile {
  filename: string;
  original_filename?: string | null;
  message_date?: string | null;
  cas_type?: string | null;
  size?: number | null;
  url: string;
  expires_in?: number | null;
}

export interface GmailCasConnection {
  oauthUrl: string;
  expiresIn: number | null;
}

export interface CdslCasSession {
  sessionId: string;
  message: string;
}

export interface CdslCasVerification {
  files: CasExternalFile[];
  message: string;
}

export interface ExternalMutualFundHolding {
  key: string;
  source: "demat" | "folio";
  isin: string;
  name: string;
  units: number;
  currentValue: number;
  investedValue: number | null;
  gainAmount: number | null;
  gainPercentage: number | null;
  nav: number | null;
  folioNumber: string | null;
  accountReference: string | null;
  amc: string | null;
  type: string | null;
}

export interface ExternalMutualFundPortfolio {
  holdings: ExternalMutualFundHolding[];
  totalCurrentValue: number;
  dematHoldingCount: number;
  folioHoldingCount: number;
  statementType: string | null;
  generatedAt: string | null;
  periodFrom: string | null;
  periodTo: string | null;
}

export const mfExternalCasQueryKey = (userUuid?: string) => [
  "mfPortfolio",
  "externalCas",
  userUuid ?? "anonymous",
];

const finiteNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const optionalNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const cleanText = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const parseCasApiError = (
  payload: unknown,
  fallbackMessage: string
): MFExternalCasErrorInfo => {
  if (typeof payload === "string") {
    const text = payload.trim();
    if (!text) {
      return { message: fallbackMessage, requiresReconnect: false };
    }
    try {
      return parseCasApiError(JSON.parse(text), fallbackMessage);
    } catch {
      return { message: text, requiresReconnect: false };
    }
  }

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const nestedError =
      record.error !== undefined
        ? parseCasApiError(record.error, fallbackMessage)
        : null;
    const directMessage =
      cleanText(record.msg) || cleanText(record.message) || null;
    return {
      message: directMessage || nestedError?.message || fallbackMessage,
      requiresReconnect:
        record.requires_reconnect === true ||
        nestedError?.requiresReconnect === true,
    };
  }

  return { message: fallbackMessage, requiresReconnect: false };
};

class CasApiError extends Error {
  requiresReconnect: boolean;

  constructor({ message, requiresReconnect }: MFExternalCasErrorInfo) {
    super(message);
    this.name = "CasApiError";
    this.requiresReconnect = requiresReconnect;
  }
}

const emptyExternalPortfolio = (): ExternalMutualFundPortfolio => ({
  holdings: [],
  totalCurrentValue: 0,
  dematHoldingCount: 0,
  folioHoldingCount: 0,
  statementType: null,
  generatedAt: null,
  periodFrom: null,
  periodTo: null,
});

const isStoredCasData = (
  data: MFExternalCasData | MFExternalCasStoredData
): data is MFExternalCasStoredData => "cas_data" in data;

const resolveExternalCasData = (
  response?: MFExternalCasResponse | null
): MFExternalCasData | null => {
  const data = response?.data;
  if (!data) return null;
  return isStoredCasData(data) ? data.cas_data ?? null : data;
};

export const buildExternalMutualFundPortfolio = (
  response?: MFExternalCasResponse | null
): ExternalMutualFundPortfolio => {
  const data = resolveExternalCasData(response);
  if (!data) return emptyExternalPortfolio();

  const holdings: ExternalMutualFundHolding[] = [];

  (data.demat_accounts ?? []).forEach((account, accountIndex) => {
    const accountReference =
      cleanText(account.bo_id) ||
      cleanText(account.dp_name) ||
      `demat-${accountIndex + 1}`;

    (account.holdings?.demat_mutual_funds ?? []).forEach(
      (holding, holdingIndex) => {
        const isin = cleanText(holding.isin);
        if (!isin) return;
        holdings.push({
          key: `demat:${accountReference}:${isin}:${holdingIndex}`,
          source: "demat",
          isin,
          name: cleanText(holding.name) ?? isin,
          units: finiteNumber(holding.units),
          currentValue: finiteNumber(holding.value),
          investedValue: null,
          gainAmount: null,
          gainPercentage: null,
          nav: null,
          folioNumber: null,
          accountReference,
          amc: cleanText(account.dp_name),
          type: cleanText(account.demat_type),
        });
      }
    );
  });

  (data.mutual_funds ?? []).forEach((folio, folioIndex) => {
    const folioNumber =
      cleanText(folio.folio_number) || `folio-${folioIndex + 1}`;
    (folio.schemes ?? []).forEach((scheme, schemeIndex) => {
      const isin = cleanText(scheme.isin);
      if (!isin) return;
      holdings.push({
        key: `folio:${folioNumber}:${isin}:${schemeIndex}`,
        source: "folio",
        isin,
        name: cleanText(scheme.name) ?? isin,
        units: finiteNumber(scheme.units),
        currentValue: finiteNumber(scheme.value),
        investedValue: optionalNumber(scheme.cost),
        gainAmount: optionalNumber(scheme.gain?.absolute),
        gainPercentage: optionalNumber(scheme.gain?.percentage),
        nav: optionalNumber(scheme.nav),
        folioNumber,
        accountReference: cleanText(folio.registrar),
        amc: cleanText(folio.amc),
        type: cleanText(scheme.type),
      });
    });
  });

  holdings.sort((a, b) => b.currentValue - a.currentValue);

  return {
    holdings,
    totalCurrentValue: holdings.reduce(
      (sum, holding) => sum + holding.currentValue,
      0
    ),
    dematHoldingCount: holdings.filter(
      (holding) => holding.source === "demat"
    ).length,
    folioHoldingCount: holdings.filter(
      (holding) => holding.source === "folio"
    ).length,
    statementType: cleanText(data.meta?.cas_type),
    generatedAt: cleanText(data.meta?.generated_at),
    periodFrom: cleanText(data.meta?.statement_period?.from),
    periodTo: cleanText(data.meta?.statement_period?.to),
  };
};

const assertSuccessfulResponse = (
  response: MFExternalCasResponse,
  fallbackMessage: string
) => {
  if (response.success === false || response.success1 === false) {
    throw new Error(response.message || fallbackMessage);
  }
};

const assertSuccessfulEnvelope = <T>(
  response: CasApiEnvelope<T>,
  fallbackMessage: string
) => {
  if (response.success === false) {
    throw new CasApiError(parseCasApiError(response, fallbackMessage));
  }
};

export const fetchMFExternalCas = async (
  userUuid: string
): Promise<MFExternalCasResponse | null> => {
  try {
    const response: any = await makeGETRequestOnUserService(
      `/mutual_fund/mf_external/user/${encodeURIComponent(userUuid)}/`
    );
    const data = response?.data as MFExternalCasResponse;
    assertSuccessfulResponse(data, "External portfolio data could not be loaded.");
    const responseUserId =
      cleanText(data.user_id) ||
      (data.data && isStoredCasData(data.data)
        ? cleanText(data.data.user_id)
        : null);
    if (responseUserId && responseUserId !== userUuid) {
      throw new Error(
        "The external portfolio data was not linked to this investor."
      );
    }
    return data;
  } catch (error) {
    if ((error as any)?.response?.status === 404) return null;
    throw error;
  }
};

export const parseMFExternalCasFile = async (
  userUuid: string,
  file: File
): Promise<MFExternalCasResponse> => {
  const payload = new FormData();
  payload.append("user_id", userUuid);
  payload.append("file", file);

  const response: any = await makePOSTRequestOnUserService(
    "/mutual_fund/mf_external/cas/parse/",
    payload
  );
  const data = response?.data as MFExternalCasResponse;
  assertSuccessfulResponse(data, "The CAS statement could not be parsed.");
  if (data.user_id && data.user_id !== userUuid) {
    throw new Error("The imported CAS was not linked to this investor.");
  }
  return data;
};

export const parseMFExternalCasUrl = async (
  userUuid: string,
  pdfUrl: string
): Promise<MFExternalCasResponse> => {
  const response: any = await makePOSTRequestOnUserService(
    "/mutual_fund/mf_external/cas/parse/",
    { user_id: userUuid, pdf_url: pdfUrl }
  );
  const data = response?.data as MFExternalCasResponse;
  assertSuccessfulResponse(data, "The CAS statement URL could not be parsed.");
  if (data.user_id && data.user_id !== userUuid) {
    throw new Error("The imported CAS was not linked to this investor.");
  }
  return data;
};

export const generateCasByEmail = async ({
  email,
  fromDate,
  toDate,
  pan,
}: {
  email: string;
  fromDate: string;
  toDate: string;
  pan: string;
}): Promise<string> => {
  const response: any = await makePOSTRequestOnUserService(
    "/mutual_fund/mf_external/cas/generate/",
    { email, from_date: fromDate, to_date: toDate, pan_no: pan }
  );
  const data = response?.data as CasApiEnvelope<{ msg?: string }>;
  assertSuccessfulEnvelope(data, "The CAS email request could not be submitted.");
  return (
    cleanText(data.data?.msg) ||
    data.message ||
    "CAS requested. It will arrive at the investor’s registered email."
  );
};

export const getMFExternalCasErrorInfo = (
  error: unknown
): MFExternalCasErrorInfo => {
  if (error instanceof CasApiError) {
    return {
      message: error.message,
      requiresReconnect: error.requiresReconnect,
    };
  }
  const asResponse = error as { response?: { data?: unknown }; message?: string };
  if (asResponse?.response) {
    return parseCasApiError(
      asResponse.response.data,
      asResponse.message || "The CAS statement could not be imported."
    );
  }
  return {
    message:
      error instanceof Error
        ? error.message
        : "The CAS statement could not be imported.",
    requiresReconnect: false,
  };
};

export const getMFExternalCasError = (error: unknown): string =>
  getMFExternalCasErrorInfo(error).message;
